import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GET_FISCAL_YEAR, GetFiscalYearQuery, GetFiscalYearQueryVariables } from '../graphql';

export const useFiscalYear = ({
  fiscalYearId,
}: {
  fiscalYearId: string | null;
}): {
  fiscalYear: GetFiscalYearQuery['getFiscalYear'] | null;
  loading: boolean;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
} => {
  const shouldSkip = !fiscalYearId;
  const { data, error, loading, startPolling, stopPolling } = useQuery<GetFiscalYearQuery, GetFiscalYearQueryVariables>(
    GET_FISCAL_YEAR,
    shouldSkip ? { skip: true } : { variables: { fiscalYearId } },
  );

  if (loading) {
    return { fiscalYear: null, loading: true, startPolling, stopPolling };
  }
  if (error) {
    throw error;
  }
  if (shouldSkip) {
    return { fiscalYear: null, loading: false, startPolling, stopPolling };
  }
  if (!data && !shouldSkip) {
    throw createError('MissingFiscalYear', 'Failed to load fiscal year');
  }

  return {
    fiscalYear: data?.getFiscalYear ?? null,
    loading,
    startPolling,
    stopPolling,
  };
};

export const useFiscalYearOrFail = ({
  fiscalYearId,
}: {
  fiscalYearId: string;
}): {
  fiscalYear: NonNullable<GetFiscalYearQuery['getFiscalYear']>;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
} => {
  const { fiscalYear, startPolling, stopPolling } = useFiscalYear({ fiscalYearId });
  if (!fiscalYear) {
    throw createError('MissingFiscalYear', 'Failed to load fiscal year');
  }
  return { fiscalYear, startPolling, stopPolling };
};
